import React from "react";
import {
  makeThemedStyles,
  hexToRgba,
} from "@qpr/client-api-base/viewcomponents/utils";
import { ICardElementWithChildren } from "backend/model/model";
import CardWrapper from "./InitiativeDetailsCardWrapper";

const useStylesForPerf = makeThemedStyles(
  "KPIDetailsContent.Performance",
  (theme) => ({
    rootcontainer: {
      display: "flex",
      flexDirection: "column",
    },
    headerStyle: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "14.63px",
      margin: "14px auto",
      selfAlign: "center",
      fontFamily: "Montserrat",
      fontStyle: "normal",
      color: "#4C5157",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "4px auto",
      width: "auto",
      height: "23px",
      background: "#E1F5FF",
      borderRadius: "15px",
    },
    Year: {
      marginLeft: "10px",
    },
    YearText: {
      color: "rgba(76, 81, 87)",
    },
    ProsContainer: {
      marginLeft: "10px",
    },
    ValuesRow: {
      gap: "20px",
    },
    ValuesTxt: {
      marginLeft: "0px",
    },
    dataContainer: {
      width: "40%",
      flexDirection: "column",
      marginLeft: "2.74em",
      color: "#656A72",
      display: "flex",
      gap: "1.2em",
    },
    donutChartContainer: {
      width: "60%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    periodStyles: {
      color: "rgba(76, 81, 87, 1)",
      fontSize: "2em",
      lineHeight: "2.28em",
    },
    text: {
      fontSize: "24px",
      color: "rgba(17, 94, 103, 1)",
      lineHeight: "0.9em",
      marginLeft: "-75px",
      marginTop: "5px",
    },
    perfStyle: {
      display: "flex",
      justifyContent: "flex-start",
    },
    valueContainer: {
      display: "flex",
      fontSize: "13px",
      lineHeight: "1.5em",
      flexWrap: "wrap",
    },
    datatext: {
      fontWeight: 500,
      marginRight: "0.8em",
    },
    datavalue: {
      fontWeight: 600,
    },
    title: {
      fontSize: "16px",
    },
  })
);

const donutTextStyle = ({ color }: { color?: string }) =>
  makeThemedStyles("KPIDetailsContent.DonutChart", (theme) => ({
    donutText: {
      fontSize: "48px",
      alignmentBaseline: "middle",
      strokeWidth: "0px",
      fill: color || "rgb(84, 84, 84, 100%)",
    },
  }));

const useStylesForDonutChart = makeThemedStyles(
  "KPIDetailsContent.DonutChart",
  (theme) => ({
    DonutChart: {
      width: "13.71em",
      height: "13.71em",
    },
    line: {
      stroke: "rgba(113, 206, 203)",
      strokeWidth: "20px",
      strokeLinecap: "round",
      fill: "none",
    },
    background: {
      fill: "rgb(250, 250, 250, 0.8)",
      strokeWidth: "20px",
      strokeLinecap: "round",
      stroke: "rgb(207, 239, 239)",
    },
    text: {
      fontSize: "30px",
      alignmentBaseline: "middle",
    },
    subtitle: {
      fontSize: "20px",
      color: "lightgray",
      alignmentBaseline: "middle",
    },
  })
);

const useTargetStyles = makeThemedStyles(
  "KPIDetailsContent.TargetCard",
  (theme) => ({
    valueContainer: {
      display: "flex",
      alignItems: "baseline",
      gap: "5px",
    },
    valueText: {
      fontSize: "3.88em",
    },
    unitText: {
      fontSize: "1.72em",
    },
  })
);

export const YearEndTargetCard = (props: {
  period: string;
  title: string;
  value?: string;
  unit?: string;
}) => {
  const { title, period, value, unit } = props;
  const styles = useTargetStyles();

  return (
    <CardWrapper title={title} period={period} color="b1b1b1">
      <div className={styles.valueContainer}>
        <div className={styles.valueText}>{value}</div>
        <div className={styles.unitText}>{unit}</div>
      </div>
    </CardWrapper>
  );
};

export const TargetAchievementCard = (
  props: React.PropsWithChildren<{
    period: string;
    title: string;
    data?: ICardElementWithChildren;
    colorKey?: keyof ICardElementWithChildren;
    series: Array<{
      title: string;
      series: keyof ICardElementWithChildren;
    }>;
  }>
) => {
  const { data, title, period, series } = props;
  const styles = useStylesForPerf();
  const alldata = React.useMemo(
    () => ({
      setcolor: data?.[props.colorKey || "rangecolor"] || "#696969",
      actual: data?.ACT_prettyvalue || "-",
      target: data?.TAR_prettyvalue || "-",
      performance: data?.PERF_prettyvalue || "-",
    }),
    [data]
  );

  return (
    <CardWrapper
      title={title}
      period={period}
      color={String(alldata.setcolor)}
      topSpace
    >
      <>
        <div className={styles.donutChartContainer}>{props.children}</div>
        <div className={styles.dataContainer}>
          {series.map((ser) => {
            const value = data?.[ser.series] || "-";
            return (
              <div className={styles.valueContainer} key={ser.title}>
                <div className={styles.datatext}>{ser.title}</div>
                <div className={styles.datavalue}>{value}</div>
              </div>
            );
          })}
        </div>
      </>
    </CardWrapper>
  );
};
